import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';

import { DataElementContext } from '@/page-components/common/DataElementContext';
import { processComponentProps } from '@/page-components/utils/processComponentProps';

type SliderRangeProps = {
  children: any;
  styleText: string;
  className: string;
  properties?: any;
};

const ModuleElementDiv = styled.div<{ $styleText: string }>((props) => props.$styleText);

const SliderRange = (componentProps: SliderRangeProps) => {
  let props = componentProps;

  const dataElementContext = React.useContext(DataElementContext);
  [props] = processComponentProps(props, dataElementContext);

  const sliderValuesMapper = dataElementContext?.sliderValuesMapper;
  const [selectedValue, setSelectedValue] = useState(dataElementContext?.defaultValue || '0');
  const [selectedUnit, setSelectedUnit] = useState('');
  const [sliderValue, setSliderValue] = useState<number>(dataElementContext?.defaultValue || 0);
  
  const sliderInputRef = useRef<HTMLInputElement | null>(null);
  
  function updateRangeProgress(input: HTMLInputElement) {
    try {
      const min = parseFloat(input.min) || 0;
      const max = parseFloat(input.max) || 100;
      const value = parseFloat(input.value);
      const percentage = ((value - min) / (max - min)) * 100;

      setSliderValue(value);
      setSelectedValue(sliderValuesMapper[value]?.value || value.toString());
      setSelectedUnit(sliderValuesMapper[value]?.unit || sliderValuesMapper[max]?.unit || '');

      input.style.setProperty('--range-progress', `${percentage}%`);
    } catch (error) {
      console.error('Error updating range progress:', error);
    }
  }

  const contextValue = React.useMemo(() => {
    return {
      selectedValue,
      selectedUnit,
      sliderValue,
      labels: Object.values(sliderValuesMapper),
      ...dataElementContext
    };
  }, [selectedValue, selectedUnit, sliderValue, dataElementContext, sliderValuesMapper]);

  useEffect(() => {
    const rangeInputs = document.querySelectorAll(`.slider-input[type="range"][name="slider-range-${dataElementContext?.__id}"]`);
    const firstRangeInput = rangeInputs.length > 0 ? rangeInputs[0] as HTMLInputElement : null;

    if (firstRangeInput) {
      sliderInputRef.current = firstRangeInput;
      
      if (dataElementContext?.defaultValue) {
        firstRangeInput.value = dataElementContext.defaultValue;
      }
      
      updateRangeProgress(firstRangeInput);

      const handleInput = function(this: HTMLInputElement) {
        updateRangeProgress(this);
      };
      
      firstRangeInput.addEventListener('input', handleInput);
      firstRangeInput.addEventListener('change', handleInput);

      return () => {
        firstRangeInput.removeEventListener('input', handleInput);
        firstRangeInput.removeEventListener('change', handleInput);
      };
    } else {
      console.log('No matching range input found for selector:', `.slider-input[type="range"][name="slider-range-${dataElementContext?.__id}"]`);
    }
  }, [dataElementContext?.__id]);


  useEffect(() => {
    if (sliderInputRef.current) {
      if (parseFloat(sliderInputRef.current.value) !== sliderValue) {
        sliderInputRef.current.value = sliderValue.toString();
        
        // Update the visual appearance
        const min = parseFloat(sliderInputRef.current.min) || 0;
        const max = parseFloat(sliderInputRef.current.max) || 100;
        const percentage = ((sliderValue - min) / (max - min)) * 100;
        sliderInputRef.current.style.setProperty('--range-progress', `${percentage}%`);
      }
    }
  }, [sliderValue]);

  return (
    <ModuleElementDiv className={componentProps.className ?? ''} $styleText={componentProps.styleText}>
      <DataElementContext.Provider value={contextValue}>{componentProps.children}</DataElementContext.Provider>
    </ModuleElementDiv>
  );
};

export default SliderRange;
